import { motion } from "framer-motion";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid } from "recharts";
import KpiCard from "./KpiCard";
import { RefreshCw, Trophy, TrendingDown } from "lucide-react";

const CYAN = "#00E5FF";
const GREEN = "#39FF14";
const RED = "#FF3B30";

export default function BacktestReport({ data, onRerun, rerunLoading, onOpenStock }) {
  if (!data) return null;
  const agg = data.aggregate || {};
  const byLevel = data.by_level || {};
  const recent = data.recent_signals || [];
  const perStock = data.per_stock || [];

  const levelChartData = Object.entries(byLevel).map(([lvl, m]) => ({
    level: `₹${Number(lvl).toLocaleString("en-IN")}`,
    winRate: m.win_rate || 0,
    signals: m.signal_count || 0,
    avgPnl: m.avg_pnl_pct || 0,
  }));

  // Equity curve (cumulative sum of pnl_pct on recent signals in chronological order)
  const equityData = [...recent].reverse().reduce((acc, s, i) => {
    const prev = acc[i - 1]?.equity ?? 0;
    acc.push({ n: i + 1, equity: +(prev + s.pnl_pct).toFixed(2) });
    return acc;
  }, []);

  return (
    <section id="backtest" className="relative py-16 lg:py-24 border-t border-white/8" data-testid="backtest-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="text-[10px] font-mono tracking-widest text-[#39FF14] uppercase">02 · Backtest Report</div>
            <h2 className="mt-2 font-display text-4xl lg:text-5xl font-semibold">
              How the breakouts <span className="text-[#39FF14]">actually performed</span>
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-white/55">
              Every historical psych-level breakout on 1-hour candles across the NSE equity universe. Entry at candle close,
              stop-loss at candle low, exit after 5 trading days (35 hours) or on SL — whichever first.
            </p>
          </div>
          <button
            data-testid="backtest-rerun-btn"
            onClick={onRerun}
            disabled={rerunLoading}
            className="pill-btn inline-flex items-center gap-2 text-sm disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${rerunLoading ? "animate-spin" : ""}`} />
            {rerunLoading ? "Recomputing…" : "Re-run Backtest"}
          </button>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <KpiCard label="Total signals" value={agg.total_signals || 0} tone="cyan" testId="kpi-total-signals" />
          <KpiCard label="Win rate" value={`${agg.win_rate || 0}%`} tone={agg.win_rate >= 50 ? "green" : "red"} testId="kpi-win-rate" sub={`${agg.wins || 0}W · ${agg.losses || 0}L`} />
          <KpiCard label="Avg P&L" value={`${agg.avg_pnl_pct >= 0 ? "+" : ""}${agg.avg_pnl_pct || 0}%`} tone={agg.avg_pnl_pct >= 0 ? "green" : "red"} testId="kpi-avg-pnl" />
          <KpiCard label="Max drawdown" value={`${agg.max_drawdown_pct || 0}%`} tone="red" testId="kpi-max-dd" />
          <KpiCard label="Best trade" value={`+${agg.best_trade_pct || 0}%`} tone="green" testId="kpi-best-trade" />
          <KpiCard label="Worst trade" value={`${agg.worst_trade_pct || 0}%`} tone="red" testId="kpi-worst-trade" />
          <KpiCard label="Avg win" value={`+${agg.avg_win_pct || 0}%`} tone="green" testId="kpi-avg-win" />
          <KpiCard label="Avg loss" value={`${agg.avg_loss_pct || 0}%`} tone="red" testId="kpi-avg-loss" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          <div className="card-surface rounded-2xl p-6" data-testid="chart-win-rate">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-medium">Win rate by level</h3>
              <span className="ticker-badge text-white/50">%</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={levelChartData} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="level" tick={{ fill: "#A1A1AA", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#A1A1AA", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }} cursor={{ fill: "rgba(0,229,255,0.05)" }} />
                  <Bar dataKey="winRate" fill={CYAN} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card-surface rounded-2xl p-6" data-testid="chart-equity">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-medium">Cumulative return (arithmetic)</h3>
              <span className="ticker-badge text-white/50">% Σ P&L</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={equityData} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="n" tick={{ fill: "#A1A1AA", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#A1A1AA", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }} />
                  <Line type="monotone" dataKey="equity" stroke={GREEN} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          <TopStocks title="Top performers" icon={<Trophy className="h-4 w-4 text-[#39FF14]" />} stocks={perStock.filter(s => s.metrics.total_signals >= 2).sort((a, b) => b.metrics.avg_pnl_pct - a.metrics.avg_pnl_pct).slice(0, 6)} tone="green" onOpen={onOpenStock} testId="top-performers" />
          <TopStocks title="Weakest performers" icon={<TrendingDown className="h-4 w-4 text-[#FF3B30]" />} stocks={perStock.filter(s => s.metrics.total_signals >= 2).sort((a, b) => a.metrics.avg_pnl_pct - b.metrics.avg_pnl_pct).slice(0, 6)} tone="red" onOpen={onOpenStock} testId="worst-performers" />
        </div>

        {/* Recent signals table */}
        <div className="card-surface rounded-2xl overflow-hidden" data-testid="recent-signals-table">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
            <h3 className="font-display text-lg font-medium">Recent signals (last {Math.min(recent.length, 200)})</h3>
            <span className="ticker-badge text-white/50">chronological</span>
          </div>
          <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/8 text-[10px] font-mono tracking-widest uppercase text-white/45">
            <div className="col-span-2">Symbol</div>
            <div className="col-span-2">Signal (IST)</div>
            <div className="col-span-1 text-right">Level</div>
            <div className="col-span-1 text-right">Entry</div>
            <div className="col-span-1 text-right">SL</div>
            <div className="col-span-1 text-right">Exit</div>
            <div className="col-span-2 text-right">Reason</div>
            <div className="col-span-2 text-right">P&L %</div>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {recent.slice(0, 60).map((s, i) => (
              <motion.button
                key={i}
                onClick={() => onOpenStock?.(s.symbol)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.01 }}
                data-testid={`signal-row-${i}`}
                className="table-row-hover w-full text-left grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/5 items-center"
              >
                <div className="col-span-2"><span className="ticker-badge">{s.symbol}</span></div>
                <div className="col-span-2 font-mono text-xs text-white/60">{formatIST(s.signal_time)}</div>
                <div className="col-span-1 text-right font-mono text-xs text-[#00E5FF]">₹{s.level}</div>
                <div className="col-span-1 text-right font-mono text-xs text-white">₹{s.entry}</div>
                <div className="col-span-1 text-right font-mono text-xs text-white/50">₹{s.stop_loss}</div>
                <div className="col-span-1 text-right font-mono text-xs text-white">₹{s.exit}</div>
                <div className="col-span-2 text-right">
                  <span className={`ticker-badge ${s.exit_reason === "stop_loss" ? "text-[#FF3B30] border-[#FF3B30]/30" : "text-white/60"}`}>
                    {s.exit_reason === "stop_loss" ? "SL HIT" : "TIME EXIT"}
                  </span>
                </div>
                <div className={`col-span-2 text-right font-mono text-sm font-medium ${s.pnl_pct >= 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>
                  {s.pnl_pct >= 0 ? "+" : ""}{s.pnl_pct}%
                </div>
              </motion.button>
            ))}
            {recent.length === 0 && (
              <div className="px-5 py-12 text-center text-sm text-white/45" data-testid="signals-empty-state">
                {data.warming_up ? "Backtest in progress — check back in a minute." : "No historical signals yet."}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function TopStocks({ title, icon, stocks, tone, onOpen, testId }) {
  const cls = tone === "green" ? "text-[#39FF14]" : "text-[#FF3B30]";
  return (
    <div className="card-surface rounded-2xl p-6" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="font-display text-lg font-medium">{title}</h3>
      </div>
      <div className="space-y-2">
        {stocks.length === 0 && <div className="text-sm text-white/45 py-4">No data yet.</div>}
        {stocks.map((s) => (
          <button
            key={s.symbol}
            onClick={() => onOpen?.(s.symbol)}
            data-testid={`top-stock-${s.symbol}`}
            className="w-full text-left grid grid-cols-12 gap-2 items-center px-3 py-2.5 rounded-lg hover:bg-white/5"
          >
            <div className="col-span-4"><span className="ticker-badge">{s.symbol}</span></div>
            <div className="col-span-4 text-xs text-white/55 truncate">{s.name}</div>
            <div className="col-span-2 text-right font-mono text-xs text-white/50">{s.signal_count} sig</div>
            <div className={`col-span-2 text-right font-mono text-sm font-medium ${cls}`}>
              {s.metrics.avg_pnl_pct >= 0 ? "+" : ""}{s.metrics.avg_pnl_pct}%
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function formatIST(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return iso; }
}
